import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";

export default function IpadLogin({ setIsLoggedIn, setUserRole }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  // QR related
  const [showQR, setShowQR] = useState(false);
  const [lessons, setLessons] = useState([]);
  const [currentCode, setCurrentCode] = useState(null);
  const [currentLessonInfo, setCurrentLessonInfo] = useState("");

  const fetchTodayCodes = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/getTodayLiveAttendanceCodes`
      );
      if (res.data.success) {
        setLessons(res.data.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch attendance codes:", err);
    }
  };

  const updateCurrentCode = () => {
    if (lessons.length === 0) {
      setCurrentCode(null);
      setCurrentLessonInfo("No lesson today");
      return;
    }

    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();

    let active = null;
    for (const lesson of lessons) {
      if (!lesson.end_time) continue;
      const [h, m] = lesson.end_time.split(":").map(Number);
      const endMinutes = h * 60 + m;

      if (currentMinutes <= endMinutes) {
        active = lesson;
        break;
      }
    }

    if (!active) {
      active = lessons[lessons.length - 1];
    }

    setCurrentCode(active?.attendance_code || null);
    setCurrentLessonInfo(
      active ? `${active.start_time} - ${active.end_time}` : "No active lesson"
    );
  };

  // Fetch on mount + every 30 seconds
  useEffect(() => {
    fetchTodayCodes();
    const timer = setInterval(fetchTodayCodes, 30000);
    return () => clearInterval(timer);
  }, []);

  // Recalculate which code to show whenever lessons change + every 30s
  useEffect(() => {
    updateCurrentCode();
    const timer = setInterval(updateCurrentCode, 30000);
    return () => clearInterval(timer);
  }, [lessons]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/admin/login`, {
        username,
        password,
      });
      const { token, role } = res.data;
      localStorage.setItem("authToken", token);
      localStorage.setItem("userRole", role || "admin");
      setIsLoggedIn(true);
      setUserRole(role || "admin");
      navigate("/ipad/scanner", { replace: true });
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>iPad Login</h1>
        <p style={styles.subtitle}></p>

        <form onSubmit={handleLogin} style={styles.form}>
          <input
            type="text"
            placeholder="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            style={styles.input}
            autoComplete="username"
            required
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={styles.input}
            autoComplete="current-password"
            required
          />
          {error && <p style={styles.error}>{error}</p>}
          <button type="submit" disabled={loading} style={styles.button}>
            {loading ? "Logging in..." : "Login"}
          </button>
        </form>
      </div>

      {/* Small QR button */}
      <button
        onClick={() => {
          fetchTodayCodes();
          setShowQR(true);
        }}
        style={styles.qrButton}
        title="Show Attendance QR"
      >
        QR
      </button>

      {/* QR Modal */}
      {showQR && (
        <div style={styles.qrOverlay} onClick={() => setShowQR(false)}>
          <div style={styles.qrModal} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ margin: "0 0 8px 0", fontSize: 20 }}>
              Attendance QR Code
            </h2>
            <p style={{ margin: "0 0 20px 0", color: "#666", fontSize: 14 }}>
              {currentLessonInfo}
            </p>

            {currentCode ? (
              <div style={{ background: "white", padding: 16, borderRadius: 12 }}>
                <QRCodeSVG value={currentCode} size={220} />
              </div>
            ) : (
              <p style={{ color: "#999" }}>No attendance code available</p>
            )}

            <button onClick={() => setShowQR(false)} style={styles.closeBtn}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f0f4f8",
    padding: 20,
    position: "relative",
  },
  card: {
    background: "white",
    borderRadius: 16,
    padding: "40px 32px",
    width: "100%",
    maxWidth: 400,
    boxShadow: "0 10px 40px rgba(0,0,0,0.08)",
  },
  title: {
    margin: 0,
    fontSize: 28,
    fontWeight: 700,
    textAlign: "center",
  },
  subtitle: {
    textAlign: "center",
    color: "#666",
    marginBottom: 32,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  input: {
    padding: "14px 16px",
    fontSize: 18,
    borderRadius: 10,
    border: "1px solid #ddd",
    outline: "none",
  },
  button: {
    padding: "16px",
    fontSize: 18,
    fontWeight: 600,
    background: "#2563eb",
    color: "white",
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    marginTop: 8,
  },
  error: {
    color: "#dc2626",
    textAlign: "center",
    margin: 0,
  },
  qrButton: {
    position: "fixed",
    bottom: 24,
    right: 24,
    width: 56,
    height: 56,
    borderRadius: "50%",
    background: "#111",
    color: "white",
    border: "none",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
    zIndex: 50,
  },
  qrOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 100,
  },
  qrModal: {
    background: "white",
    borderRadius: 20,
    padding: "28px 24px",
    width: "90%",
    maxWidth: 340,
    textAlign: "center",
    boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
  },
  closeBtn: {
    marginTop: 24,
    padding: "12px 28px",
    fontSize: 16,
    fontWeight: 600,
    background: "#e5e7eb",
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
  },
};